// The signer's page: no sign-in, one link (AGREESIGN, MEET R19).
//
// The link is `/sign#<token>`. The token sits in the URL fragment, which the
// browser never sends to a server, and this page posts it in a request body,
// so it reaches no access log and no Referer. The page shows only what the
// link names: the agreement(s) this person is asked to sign, and each
// document. Signing needs an explicit consent to sign electronically and the
// person's own full name; the document fingerprint they were shown travels
// with the signature, and the server refuses it if the document changed.
import { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../auth/identity';
import { formatCalendarDate, formatViewerDate } from '../shared/date';
import { useSurfaceTheme } from '../customer/useSurfaceTheme';
import { useDeviceViewport } from '../portal/useDeviceViewport';
import * as W from './strings';
import './sign.css';

const ROOT = API_BASE.replace(/\/dash$/, '') + '/sign';

interface LinkAgreement {
  id: string;
  title: string;
  state_name: string;
  practitioner: string;
  physician: string;
  effective_date: string;
  expiration_date: string | null;
  medications: string[];
  document_sha256: string;
  open: boolean;
  already_signed: boolean;
}
interface Opened {
  signer: { name: string; role: 'provider' | 'physician' };
  expires_at: string;
  consent_text: string;
  agreements: LinkAgreement[];
}

function day(iso: string | null): string {
  if (!iso) return '';
  return iso.length === 10 ? formatCalendarDate(iso) : formatViewerDate(iso, { dateStyle: 'long' });
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(`${ROOT}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
}

async function detail(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: unknown };
    return typeof body.detail === 'string' ? body.detail : W.SIGN_ERROR;
  } catch {
    return W.SIGN_ERROR;
  }
}

export function SignPage() {
  useSurfaceTheme();
  useDeviceViewport();
  const token = useMemo(() => window.location.hash.replace(/^#/, ''), []);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [docs, setDocs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(token ? null : W.SIGN_NO_LINK);
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = `${W.SIGN_TITLE} — VerityRx`;
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!token) return;
    let live = true;
    const urls: string[] = [];
    (async () => {
      const res = await post('open', { token });
      if (!res.ok) {
        if (live) setError(await detail(res));
        return;
      }
      const body = (await res.json()) as Opened;
      if (!live) return;
      setOpened(body);
      for (const a of body.agreements.filter((x) => x.open)) {
        const pdf = await post('document', { token, agreement_id: a.id });
        if (!pdf.ok || !live) continue;
        const url = URL.createObjectURL(await pdf.blob());
        urls.push(url);
        setDocs((old) => ({ ...old, [a.id]: url }));
      }
    })().catch(() => live && setError(W.SIGN_ERROR));
    return () => {
      live = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [token]);

  const toSign = (opened?.agreements ?? []).filter((a) => a.open && !a.already_signed);

  async function sign() {
    if (!opened) return;
    setBusy(true);
    setError(null);
    try {
      const res = await post('submit', {
        token,
        signed_name: name,
        consent: true,
        documents: Object.fromEntries(toSign.map((a) => [a.id, a.document_sha256])),
      });
      if (res.ok) setDone(true);
      else setError(await detail(res));
    } catch {
      setError(W.SIGN_ERROR);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vx vx-sign">
      <main className="vx-gate vx-gate--auth">
        <div className="vx-gate__card vx-rise">
          <span className="vx-wordmark">VerityRx</span>
          <h1 style={{ marginTop: 'var(--vx-8)' }}>{done ? W.SIGN_DONE_TITLE : W.SIGN_TITLE}</h1>
          {done ? (
            <p className="vx-lead" role="status">{W.SIGN_DONE_BODY}</p>
          ) : (
            <>
              {opened && <p className="vx-eyebrow">{W.SIGN_FOR(opened.signer.name)}</p>}
              {!opened && !error && <p className="vx-small vx-muted" role="status">{W.SIGN_LOADING}</p>}
              {error && (
                <p className="vx-callout vx-callout--strong" role="alert">
                  {error}
                </p>
              )}
              {opened && (
                <>
                  <p className="vx-lead">{W.SIGN_LEAD}</p>
                  {opened.agreements.map((a) => (
                    <section className="vx-sign__doc" key={a.id} aria-labelledby={`doc-${a.id}`}>
                      <h2 id={`doc-${a.id}`}>{a.title}</h2>
                      <p className="vx-small">
                        {W.SIGN_PARTIES(a.practitioner, a.physician)} {W.SIGN_STARTS(day(a.effective_date))}{' '}
                        {W.SIGN_ENDS(a.expiration_date ? day(a.expiration_date) : null)}
                      </p>
                      {a.medications.length > 0 && (
                        <div className="vx-small">
                          <strong>{W.SIGN_MEDICATIONS}</strong>
                          <ul className="vx-sign__meds">
                            {a.medications.map((m) => (
                              <li key={m}>{m}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {!a.open && <p className="vx-small">{W.SIGN_CLOSED}</p>}
                      {a.already_signed && <p className="vx-small">{W.SIGN_ALREADY}</p>}
                      {docs[a.id] && (
                        <>
                          <iframe className="vx-sign__frame" src={docs[a.id]} title={`${W.SIGN_DOCUMENT_FRAME}: ${a.title}`} />
                          <p>
                            <a className="vx-btn vx-btn--secondary vx-btn--sm" href={docs[a.id]} target="_blank" rel="noopener noreferrer">
                              {W.SIGN_OPEN_DOCUMENT}
                            </a>
                          </p>
                        </>
                      )}
                      <p className="vx-small vx-muted vx-sign__hash">
                        {W.SIGN_FINGERPRINT}: <code>{a.document_sha256}</code>
                      </p>
                    </section>
                  ))}
                  {toSign.length > 0 && (
                    <div className="vx-sign__form">
                      <p className="vx-small">{opened.consent_text}</p>
                      <label className="vx-check">
                        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                        <span>{W.SIGN_CONSENT_LABEL}</span>
                      </label>
                      <label className="vx-field">
                        <span>{W.SIGN_NAME}</span>
                        <input className="vx-input" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} />
                        <small className="vx-field__hint">{W.SIGN_NAME_HINT(opened.signer.name)}</small>
                      </label>
                      <div>
                        <button
                          type="button"
                          className={`vx-btn vx-btn--primary${busy ? ' vx-btn--busy' : ''}`}
                          disabled={busy || !agree || name.trim().length < 2}
                          aria-busy={busy || undefined}
                          onClick={sign}
                        >
                          {W.SIGN_SUBMIT(toSign.length)}
                        </button>
                      </div>
                      <p className="vx-small vx-muted">{W.SIGN_EXPIRES(day(opened.expires_at))}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          <p className="vx-small" style={{ color: 'var(--vx-ink-4)', marginTop: 'var(--vx-8)' }}>
            {W.SIGN_TROUBLE}
          </p>
        </div>
      </main>
    </div>
  );
}
